import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Document } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface DocumentState {
  documents: Document[];
  document: Document | null;
  activeDocumentId: number | null; // ✅ Ajouté
  loading: boolean;
  error: string | null;
}

const initialState: DocumentState = {
  documents: [],
  document: null,
  activeDocumentId: null, // ✅ Ajouté
  loading: false,
  error: null,
};


// ✅ fetchDocs (anciennement fetchDocuments)
export const fetchDocs = createAsyncThunk<Document[], void, { state: RootState }>(
  'document/fetchDocs',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/documents`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch documents');
    }
  }
);

// ✅ fetchDoc par ID (anciennement fetchDocument)
export const fetchDoc = createAsyncThunk<Document, number, { state: RootState }>(
  'document/fetchDoc',
  async (documentId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/documents/${documentId}`, // ✅ Corrigé
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      console.log('success WHILE Call document', response);
      dispatch(fetchDocs());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE FETCHING Doc', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch document');
    }
  }
);

// ✅ AJOUTÉ - fetchDocByLease (pour SelectPlugLease)
export const fetchDocByLease = createAsyncThunk<Document[], number, { state: RootState }>(
  'document/fetchDocByLease',
  async (leaseId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/documents/lease/${leaseId}`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      console.log('success WHILE Call document by lease', response);
      dispatch(fetchDocs());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE FETCHING Doc by lease', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch documents by lease');
    }
  }
);

// ✅ createDoc (anciennement createDocument)
export const createDoc = createAsyncThunk<Document, Partial<Document>, { state: RootState }>(
  'document/createDoc',
  async (documentData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(
        `${baseUrlApi}/documents`,
        documentData,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      dispatch(fetchDocs());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create document');
    }
  }
);

// ✅ updateDoc (anciennement updateDocument)
export const updateDoc = createAsyncThunk<
  Document,
  { documentData: Partial<Document>; documentId: number },
  { state: RootState }
>(
  'document/updateDoc',
  async ({ documentData, documentId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(
        `${baseUrlApi}/documents/${documentId}`, // ✅ Corrigé
        documentData,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      console.log('success WHILE update Doc', response);
      dispatch(fetchDocs());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE update Doc', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update document');
    }
  }
);

// ✅ deleteDocument
export const deleteDocument = createAsyncThunk<number, number, { state: RootState }>(
  'document/deleteDocument',
  async (documentId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(
        `${baseUrlApi}/documents/${documentId}`, // ✅ Corrigé
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      console.log('success WHILE delete Document');
      dispatch(fetchDocs());
      return documentId;
    } catch (error: any) {
      console.log('ERROR WHILE delete Document', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to delete document');
    }
  }
);

// ✅ AJOUTÉ - encoursDoc (pour compatibilité)
export const encoursDoc = createAsyncThunk<Document, Document, { state: RootState }>(
  'document/encoursDoc',
  async (documentData, { rejectWithValue }) => {
    try {
      // Simplement stocker le document en cours
      return documentData;
    } catch (error: any) {
      return rejectWithValue('Failed to set current document');
    }
  }
);

const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    clearDocument: (state) => { 
      state.document = null; 
    },
    clearDocuments: (state) => { 
      state.documents = []; 
    },
    resetDocumentState: () => initialState,
  },
  extraReducers: (builder) => {
    // ✅ Gestion des états de chargement pour tous les thunks
    [
      fetchDocs, 
      fetchDoc, 
      fetchDocByLease, 
      createDoc, 
      updateDoc, 
      deleteDocument, 
      encoursDoc
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    // ✅ Cas spécifiques fulfilled
    builder
      .addCase(fetchDocs.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchDoc.fulfilled, (state, action) => {
        state.loading = false;
        state.document = action.payload;
      })
      .addCase(fetchDocByLease.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(createDoc.fulfilled, (state, action) => {
        state.loading = false;
        state.document = action.payload;
      })
      .addCase(updateDoc.fulfilled, (state, action) => {
        state.loading = false;
        state.document = action.payload;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = state.documents.filter(item => item.id !== action.payload);
      })
      .addCase(encoursDoc.fulfilled, (state, action) => {
        state.loading = false;
        state.document = action.payload;
        state.activeDocumentId = action.payload?.id || null;
      });
  },
});

export const { clearDocument, clearDocuments, resetDocumentState } = documentSlice.actions;
export default documentSlice.reducer;
